import { Users } from "lucide-react"

import { Button } from "@/clientWeb/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/clientWeb/components/ui/dialog"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"

/**
 * The prompt a participant gets when breakout rooms open.
 *
 * Two shapes, one dialog, because it is one question — "where are you going?" —
 * asked of people the room answers differently for:
 *
 *   assigned      the host chose; the room is named and there is one button
 *   self walk-in  nobody chose; the rooms are listed and they pick
 *
 * Cancelling is not refusing. It closes the prompt and nothing else, and the
 * Breakout Rooms control keeps a way back in — a host who dismissed this while
 * doing something else must not be locked out of their own meeting's rooms.
 */

export interface BreakoutJoinRoom {
  id: string
  name: string
  participants: Array<{ id: string; name: string }>
}

interface BreakoutJoinDialogProps {
  open: boolean
  /**
   * The room this participant is in right now, if any.
   *
   * Tells a first invitation apart from a move, and makes "back to the main
   * room" expressible: assigned nowhere while sitting in a room is a recall,
   * not an absence of news.
   */
  currentRoomId?: string | null
  /** Return to the main room — used when the assignment is nowhere. */
  onReturnToMain?: () => void
  onOpenChange: (open: boolean) => void
  rooms: BreakoutJoinRoom[]
  /** Set when the host has already put this participant somewhere. */
  assignedRoomId: string | null
  /** Self walk-in — the participant chooses, if they have not been assigned. */
  selfSelect: boolean
  onJoin: (roomId: string) => void
  /** A move is in flight; the room is being swapped underneath us. */
  busy?: boolean
  /** How many fit in one room; null or 0 means no limit. */
  capacity?: number | null
}

export function BreakoutJoinDialog({
  open,
  onOpenChange,
  rooms,
  assignedRoomId,
  selfSelect,
  onJoin,
  currentRoomId = null,
  onReturnToMain,
  busy = false,
  capacity = null,
}: BreakoutJoinDialogProps) {
  const t = useTranslation()
  const assigned = assignedRoomId ? rooms.find((r) => r.id === assignedRoomId) : undefined

  // An assignment settles it. Self walk-in decides who MAY choose, not who
  // chooses over the host: once somebody has been put in a room, that is their
  // room, and the list would only offer them a way to undo the host's decision.
  const choosing = selfSelect && !assigned && !currentRoomId

  // Assigned nowhere while sitting in a breakout: the host has moved them back
  // to the main room, and the prompt is how they are told.
  const recalled = !assignedRoomId && !!currentRoomId

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("controlBar.breakoutStartedTitle")}</DialogTitle>
          <DialogDescription>
            {recalled
              ? t("controlBar.movedToMainRoom")
              : choosing
                ? t("controlBar.chooseARoom")
                : assigned
                  ? `${t("controlBar.assignedToRoom")} ${assigned.name}.`
                  : t("controlBar.breakoutRooms")}
          </DialogDescription>
        </DialogHeader>

        {choosing ? (
          <div className="mt-2 flex max-h-72 flex-col gap-2 overflow-y-auto">
            {rooms.map((room) => {
              // A full room is shown as full and cannot be picked. Refusing
              // afterwards would be a click that looks like it worked.
              const full = !!capacity && capacity > 0 && room.participants.length >= capacity
              return (
                <button
                  key={room.id}
                  type="button"
                  disabled={busy || full}
                  onClick={() => onJoin(room.id)}
                  className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5 text-left transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <span className="text-sm font-medium">{room.name}</span>
                  {/* How full it is — the only thing that makes one room a
                      different choice from another. */}
                  <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Users className="h-3.5 w-3.5" />
                    {capacity && capacity > 0
                      ? `${room.participants.length}/${capacity}`
                      : room.participants.length}
                    {full && <span className="ml-1 font-medium">{t("controlBar.roomFull")}</span>}
                  </span>
                </button>
              )
            })}
            {rooms.length === 0 && (
              <p className="py-6 text-center text-sm text-muted-foreground">
                {t("controlBar.breakoutRooms")}
              </p>
            )}
          </div>
        ) : null}

        <div className="mt-4 flex flex-col gap-2 sm:flex-row-reverse">
          {recalled ? (
            <Button
              className="w-full sm:w-auto"
              disabled={busy}
              onClick={() => onReturnToMain?.()}
            >
              {t("controlBar.mainRoom")}
            </Button>
          ) : !choosing ? (
            <Button
              className="w-full sm:w-auto"
              disabled={busy || !assigned}
              onClick={() => assigned && onJoin(assigned.id)}
            >
              {/* "Move" once they are already in a room — they are not joining
                  the breakout, they are changing which one they are in. */}
              {currentRoomId ? t("controlBar.moveToRoom") : t("controlBar.joinRoom")}
            </Button>
          ) : null}
          <Button
            variant="outline"
            className="w-full sm:w-auto"
            disabled={busy}
            onClick={() => onOpenChange(false)}
          >
            {t("controlBar.notNow")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
